import React, { } from 'react';
import { BackgroundVideo, DotaPlusVideo } from "../components/ui/file-contents";
import { PageContainer } from '../components/layout/page';
import { localize } from '../../common/services/localization';
import { SubButton } from '../components/functional/buttons';
import { setGlobalState, SubscribeGlobalState } from '../../common/hooks/global-state';
import { toDate, toDateString } from '../../common/utils';
import { AuthRepository } from '../../api/repositories/backend/auth';
import { PaymentsRepository } from '../../api/repositories/backend/payments';
import { AuthorizedUser } from '../../api/repositories/backend/models/auth';

export const Home: React.FC = () => {
    return (
        <PageContainer
            staticBg={true}
            footerClassName='mt-[0px]'
            containerClassName="mt-[40px] lg:mt-[73px]"
            child={
                <HomeContainer />
            } />
    );
};

export const HomeContainer: React.FC = () => {
    return (
        <div className="flex flex-col mt-[0px]">
            <BackgroundVideo
                videoSrc={'/v2/videos/dota-plus-header.mp4'}
            />
            <div className='flex flex-col gap-[28px] w-[1560px] mx-[10px] xl:mx-auto'>
                <h1 className='uppercase shadow-text-dark'>
                    {localize('dota_plus_title')}
                </h1>
                <p className='text-[18px] md:text-[22px] lg:text-[28px] xl:text-[32px] uppercase font-semibold shadow-text-dark'>
                    {localize('dota_plus_title_sup')}
                </p>
            </div>
            <DotaPlusSubBlock />
            <div className='flex flex-col mt-[100px] md:mt-[120px] lg:mt-[180px] w-full'>
                <h2 className='text-center'>
                    {localize('dota_plus_what_gives')}
                </h2>
            </div>
            <DotaPlusContainer />
        </div>
    );
};

const DotaPlusSubBlock: React.FC = () => {
    const authorized = SubscribeGlobalState("authorizedUser") as AuthorizedUser | undefined

    const repository = new PaymentsRepository();
    const extend = authorized && authorized.dotaPlusExpireDate && authorized.dotaPlusExpireDate > Date.now()
    let expireStatus = ''

    if (authorized && authorized.dotaPlusExpireDate) {

        if (authorized.dotaPlusExpireDate < 0) {
            expireStatus = '';
        }
        else {
            const date = new Date(authorized.dotaPlusExpireDate)
            expireStatus = localize(
                authorized.dotaPlusExpireDate < Date.now()
                    ? "dota_plus_expired_date"
                    : "dota_plus_expire_date"
            ).replace("%date%", toDateString(date))
        }
    }

    return <div className='mt-[30px] lg:mt-[50px] xl:mt-[80px] flex flex-col gap-[8px] md:gap-[16px] w-[1560px] mx-[10px] xl:mx-auto'>
        <div className='text-[12px] md:text-[16px] uppercase font-semibold shadow-text-dark'
            dangerouslySetInnerHTML={{ __html: expireStatus }}
        >
        </div>
        <div className='flex flex-col items-start gap-[4px] md:gap-[8px]'>
            <SubButton
                isAuthorized={authorized?.authorized}
                localText={extend ? "dota_plus_subscribe_extend" : "dota_plus_subscribe"}
                className='w-[280px] md:w-[455px]'
            />
            {
                authorized && authorized.authorized && authorized.isSubscribed && <button
                    className="text-gray font-semibold hover:text-white text-[12px] lg:text-[18px]"
                    onClick={async () => {
                        repository.unsubscribe()
                            .then(t => {
                                setGlobalState("authorizedUser", Object.assign({}, authorized, { isSubscribed: false }))
                            })
                    }}
                >{localize("unsubscribe")}</button>
            }
        </div>
    </div>
}


const DotaPlusContainer: React.FC = () => {
    const rowsCount = 8;
    const rows = [] as DotaPlusRow[];
    for (let index = 1; index < rowsCount + 1; index++) {
        rows.push({
            number: index,
            reverse: index % 2 == 0
        })
    }

    return <div className='flex flex-col items-center'>
        {rows.map((r, i) => <DotaPlusItemRowBlock isLast={i == rows.length - 1} isFirst={i == 0} key={i} row={r} />)}
    </div>
}

const DotaPlusItemRowBlock: React.FC<{
    row: DotaPlusRow
    isFirst: boolean
    isLast: boolean
}> = ({ row, isFirst, isLast }) => {
    const dividerClass = `var(--Divider, linear-gradient(${row.reverse ? '270deg' : '-270deg'},#18282d 0%, rgba(18, 34, 39, 0.00) 66.87%))`

    return <div className='relative py-[60px] w-full'>
        <div className={`absolute top-0 left-0 w-full h-1/2 opacity-25 ${!isFirst && 'bg-dark-split'}`}>
        </div>
        <DotaPlusItemBlock row={row} />
        <div className={`absolute bottom-0 left-0 w-full h-1/2 opacity-25  ${!isLast && 'bg-dark-split-reverse'}`}>
        </div>
        {
            !isLast && <div
                className={`absolute bottom-0 left-0 h-[3px] w-full`}
                style={{
                    background: dividerClass
                }}
            />
        }
    </div>
}

const DotaPlusItemBlock: React.FC<{
    row: DotaPlusRow
}> = ({ row }) => {
    const isReversed = row.reverse ? 'flex-row-reverse' : 'flex-row';

    return (
        <div className={`flex ${isReversed} gap-[60px] justify-center items-center relative lg:static`}>
            <div className="flex flex-col gap-[26px] w-[400px] md:w-[530px] absolute lg:static z-10">
                <div
                    className="text-[16px] md:text-[26px] w-[250px] mx-auto sm:w-full font-semibold uppercase"
                    dangerouslySetInnerHTML={{ __html: localize(`dota_plus_${row.number}title`) }}
                />
                <div
                    className="font-['Roboto'] text-[14px] w-[250px] mx-auto sm:w-full md:text-[20px]"
                    dangerouslySetInnerHTML={{ __html: localize(`dota_plus_${row.number}desc`) }}
                />
            </div>
            <div className=''>
                <DotaPlusVideo number={row.number} />
            </div>
        </div>
    );
};


interface DotaPlusRow {
    number: number
    reverse: boolean
}


